import {
  Box,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React from "react";
import {
  ALL_PERMISSION_PAGES,
  PERMISSION_ACTIONS,
  PERMISSION_MODULES,
  PermissionMatrixValue,
  PermissionOperation,
  PermissionPage,
} from "./rolePermissionConfig";

interface PermissionMatrixProps {
  value: PermissionMatrixValue;
  onChange: (value: PermissionMatrixValue) => void;
  disabled?: boolean;
}

// Tri-state helper: all checked / some checked / none checked.
const checkState = (values: boolean[]) => ({
  checked: values.length > 0 && values.every(Boolean),
  indeterminate: values.some(Boolean) && !values.every(Boolean),
});

const labelSx = { fontSize: "0.75rem" };
const headerSx = { fontSize: "0.75rem", fontWeight: "bold" };

/** Page × action grid of permission checkboxes, grouped by module. */
export const PermissionMatrix: React.FC<PermissionMatrixProps> = ({ value, onChange, disabled }) => {
  const isGranted = (page: string, op: PermissionOperation) => !!value[page]?.[op];

  const update = (changes: { page: string; op: PermissionOperation }[], granted: boolean) => {
    const next: PermissionMatrixValue = { ...value };
    changes.forEach(({ page, op }) => {
      next[page] = { ...next[page], [op]: granted };
    });
    onChange(next);
  };

  // Granting any action without View is meaningless, so Add/Edit/Delete/Export also grant View,
  // and removing View removes the page's other actions.
  const togglePermission = (page: PermissionPage, op: PermissionOperation, granted: boolean) => {
    if (op === "view" && !granted) {
      update(page.actions.map((a) => ({ page: page.key, op: a })), false);
    } else if (op !== "view" && granted && page.actions.includes("view")) {
      update([{ page: page.key, op }, { page: page.key, op: "view" }], true);
    } else {
      update([{ page: page.key, op }], granted);
    }
  };

  const toggleRow = (page: PermissionPage, granted: boolean) =>
    update(page.actions.map((op) => ({ page: page.key, op })), granted);

  const toggleColumn = (op: PermissionOperation, granted: boolean) => {
    const pages = ALL_PERMISSION_PAGES.filter((p) => p.actions.includes(op));
    const changes = pages.map((p) => ({ page: p.key, op }));
    if (op !== "view" && granted) {
      changes.push(...pages.filter((p) => p.actions.includes("view")).map((p) => ({ page: p.key, op: "view" as PermissionOperation })));
    }
    if (op === "view" && !granted) {
      changes.push(...pages.flatMap((p) => p.actions.map((a) => ({ page: p.key, op: a }))));
    }
    update(changes, granted);
  };

  const allValues = ALL_PERMISSION_PAGES.flatMap((p) => p.actions.map((op) => isGranted(p.key, op)));

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small" stickyHeader aria-label="Role permissions">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                {...checkState(allValues)}
                disabled={disabled}
                onChange={(e) => update(ALL_PERMISSION_PAGES.flatMap((p) => p.actions.map((op) => ({ page: p.key, op }))), e.target.checked)}
                inputProps={{ "aria-label": "Select all permissions" }}
              />
            </TableCell>
            <TableCell>
              <Typography sx={headerSx}>PAGE</Typography>
            </TableCell>
            {PERMISSION_ACTIONS.map((action) => {
              const column = ALL_PERMISSION_PAGES.filter((p) => p.actions.includes(action.key)).map((p) => isGranted(p.key, action.key));
              return (
                <TableCell key={action.key} align="center">
                  <Box display="flex" flexDirection="column" alignItems="center">
                    <Typography sx={headerSx}>{action.label.toUpperCase()}</Typography>
                    <Checkbox
                      size="small"
                      {...checkState(column)}
                      disabled={disabled}
                      onChange={(e) => toggleColumn(action.key, e.target.checked)}
                      inputProps={{ "aria-label": `Select ${action.label} for all pages` }}
                    />
                  </Box>
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {PERMISSION_MODULES.map((module) => (
            <React.Fragment key={module.label}>
              <TableRow>
                <TableCell colSpan={PERMISSION_ACTIONS.length + 2} sx={{ backgroundColor: "whitesmoke" }}>
                  <Typography sx={{ ...headerSx, color: "#098963" }}>{module.label}</Typography>
                </TableCell>
              </TableRow>
              {module.pages.map((page) => (
                <TableRow key={page.key} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      {...checkState(page.actions.map((op) => isGranted(page.key, op)))}
                      disabled={disabled}
                      onChange={(e) => toggleRow(page, e.target.checked)}
                      inputProps={{ "aria-label": `Select all permissions for ${page.label}` }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography sx={labelSx}>{page.label}</Typography>
                    {page.hint && <Typography sx={{ fontSize: "0.7rem", color: "text.secondary" }}>{page.hint}</Typography>}
                  </TableCell>
                  {PERMISSION_ACTIONS.map((action) => (
                    <TableCell key={action.key} align="center">
                      {page.actions.includes(action.key) ? (
                        <Checkbox
                          size="small"
                          checked={isGranted(page.key, action.key)}
                          disabled={disabled}
                          onChange={(e) => togglePermission(page, action.key, e.target.checked)}
                          inputProps={{ "aria-label": `${action.label} ${page.label}` }}
                        />
                      ) : (
                        <Typography sx={{ ...labelSx, color: "text.disabled" }} aria-label="Not applicable">—</Typography>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
